import { useAuth0 } from '@auth0/auth0-react'
import { jwtDecode } from 'jwt-decode'
import { useEffect, useState } from 'react'
import { AspenPermissions } from '../utils'

/**
 * Aspen's own permissions, read from aspen's own access token.
 *
 * ## Why this does not use `@om1/platform-authentication`
 *
 * It used to, and that made every permission check silently fail. The platform
 * hook requests a token for a **different audience** — `${domain}/explorer`,
 * with the platform scopes — so the `permissions` claim it decodes belongs to
 * explorer, not aspen. An aspen permission like `manage:candidates` is simply
 * not in it, and for a user with no explorer access the request throws and the
 * hook falls back to an empty array.
 *
 * Either way `canAccess` was always false, so anything gated on it rendered
 * nothing at all — with no error and nothing in the console to explain it.
 *
 * `getAccessTokenSilently()` with no arguments returns aspen's default-audience
 * token, whose `permissions` claim carries `access:aspen`, `admin:aspen` and
 * `manage:candidates`. That is exactly what `NavBar` already does to decide
 * which nav items to show, which is why the sidebar was right while the
 * in-page checks were wrong.
 */

type AspenTokenClaims = {
    permissions?: string[]
}

/** Developer-facing log text, not user copy — hoisted so the lingui lint rule
 * does not read it as an untranslated string. */
const TOKEN_READ_FAILED = 'Could not read aspen permissions from the access token:'

/** `undefined` until the token has been read, so callers can tell "not yet known" from "none". */
export const useAspenPermissions = (): { permissions: string[] | undefined } => {
    const { getAccessTokenSilently } = useAuth0()
    const [permissions, setPermissions] = useState<string[] | undefined>(undefined)

    useEffect(() => {
        let current = true

        // try/catch inside an async function rather than `.catch()`, because
        // `getAccessTokenSilently` can throw **synchronously** — auth0-react's
        // default context stub does exactly that when no provider is mounted, and
        // a promise-only handler would let that escape and take the page down.
        const read = async () => {
            try {
                const token = await getAccessTokenSilently()
                if (!current) return
                setPermissions(jwtDecode<AspenTokenClaims>(String(token)).permissions ?? [])
            } catch (error) {
                if (!current) return
                // Deny rather than guess. An unreadable token must not be read as
                // "has everything", and the log is the only trace an operator gets.
                // eslint-disable-next-line no-console
                console.error(TOKEN_READ_FAILED, error)
                setPermissions([])
            }
        }

        read()

        return () => {
            current = false
        }
    }, [getAccessTokenSilently])

    return { permissions }
}

export const useUserCanAccess = (requiredPermissions: AspenPermissions[]) => {
    const { permissions } = useAspenPermissions()
    return { canAccess: permissions !== undefined && requiredPermissions.every((permission) => permissions.includes(permission)) }
}
